import { useEffect, useState } from 'react'
import { ChevronRight, Image, Layers, Pencil, Printer, Package, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { AssignDatesModal, type AssignDatesTarget } from '@/components/features/tasks/AssignDatesModal'
import { BrowserPrintPreviewModal } from '@/components/features/print/BrowserPrintPreviewModal'
import { PrintKmModal } from '@/components/features/tasks/PrintKmModal'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePrintActions } from '@/hooks/usePrint'
import { useTaskBoxes, useTaskKMCodes, useTaskPallets } from '@/hooks/useTaskEntities'
import { normalizeDate } from '@/lib/utils'
import {
  TASK_ENTITY_PAGE_SIZE,
  type TaskBoxRow,
  type TaskEntityTab,
  type TaskKMRow,
  type TaskPalletRow,
  type TaskSummary,
} from '@/types'

interface TaskEntitiesPanelProps {
  task: TaskSummary
}

const TAB_LABELS: Record<TaskEntityTab, string> = {
  codes: 'Коды маркировки',
  boxes: 'Коробки',
  pallets: 'Паллеты',
}

function TablePagination({
  total,
  page,
  onPageChange,
}: {
  total: number
  page: number
  onPageChange: (page: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / TASK_ENTITY_PAGE_SIZE))
  if (totalPages <= 1) return null

  const from = (page - 1) * TASK_ENTITY_PAGE_SIZE + 1
  const to = Math.min(page * TASK_ENTITY_PAGE_SIZE, total)

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm text-gray-600">
      <span>
        {from}–{to} из {total}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="px-3 py-1 rounded border bg-white disabled:opacity-40 hover:bg-gray-100"
        >
          Назад
        </button>
        <span className="px-3 py-1">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="px-3 py-1 rounded border bg-white disabled:opacity-40 hover:bg-gray-100"
        >
          Вперёд
        </button>
      </div>
    </div>
  )
}

function PrintedCell({ printed }: { printed: boolean }) {
  return printed ? (
    <span className="text-green-600 font-medium">Да</span>
  ) : (
    <span className="text-gray-400">Нет</span>
  )
}

function WorkerCell({ worker }: { worker?: string }) {
  return <span className="text-gray-400">{worker?.trim() ? worker : '—'}</span>
}

function ActionButtons({
  onEditDates,
  onPrint,
  onPrintBundle,
  printBundleTitle,
  onPrintPalletBoxes,
  disabled,
}: {
  onEditDates?: () => void
  onPrint?: () => void
  onPrintBundle?: () => void
  printBundleTitle?: string
  onPrintPalletBoxes?: () => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
        title="Изменить даты"
        onClick={onEditDates}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      {onPrint && (
        <button
          type="button"
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
          title="Печать КА"
          onClick={onPrint}
        >
          <Printer className="w-3.5 h-3.5" />
        </button>
      )}
      {onPrintBundle && (
        <button
          type="button"
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
          title={printBundleTitle ?? 'Печать КМ+КА'}
          onClick={onPrintBundle}
        >
          <span className="text-[10px] font-bold px-0.5">КМ+КА</span>
        </button>
      )}
      {onPrintPalletBoxes && (
        <button
          type="button"
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
          title="Печать КА всех коробок паллеты"
          onClick={onPrintPalletBoxes}
        >
          <Layers className="w-3.5 h-3.5" />
        </button>
      )}
      <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Содержимое">
        <Package className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function CodesTable({ rows }: { rows: TaskKMRow[] }) {
  if (!rows.length) {
    return <EmptyState text="Нет кодов маркировки" />
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <tr>
          <th className="px-3 py-2 w-8" />
          <th className="px-4 py-2">№</th>
          <th className="px-4 py-2">Код маркировки (КМ)</th>
          <th className="px-4 py-2">GTIN</th>
          <th className="px-4 py-2">Дата производства</th>
          <th className="px-4 py-2">Срок годности</th>
          <th className="px-4 py-2">Напечатан</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.num} className="hover:bg-gray-50">
            <td className="px-3 py-2 text-gray-300">
              <ChevronRight className="w-4 h-4" />
            </td>
            <td className="px-4 py-2">{row.num}</td>
            <td className="px-4 py-2 font-mono text-xs break-all">{row.code}</td>
            <td className="px-4 py-2 font-mono text-xs">{row.gtin}</td>
            <td className="px-4 py-2">{normalizeDate(row.production_date) || '—'}</td>
            <td className="px-4 py-2">{normalizeDate(row.expiry_date) || '—'}</td>
            <td className="px-4 py-2">
              <PrintedCell printed={row.printed} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function BoxesTable({
  rows,
  onEditDates,
  onPrintBox,
  onPrintBoxBundle,
  printing,
}: {
  rows: TaskBoxRow[]
  onEditDates: (row: TaskBoxRow) => void
  onPrintBox: (row: TaskBoxRow) => void
  onPrintBoxBundle?: (row: TaskBoxRow) => void
  printing: boolean
}) {
  if (!rows.length) {
    return <EmptyState text="Нет коробок" />
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <tr>
          <th className="px-3 py-2 w-8" />
          <th className="px-4 py-2">№</th>
          <th className="px-4 py-2">Код агрегации (КА)</th>
          <th className="px-4 py-2">GTIN</th>
          <th className="px-4 py-2">Паллета</th>
          <th className="px-4 py-2">Дата производства</th>
          <th className="px-4 py-2">Срок годности</th>
          <th className="px-4 py-2">Напечатана</th>
          <th className="px-4 py-2">Работник</th>
          <th className="px-4 py-2">Действия</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.num} className="hover:bg-gray-50">
            <td className="px-3 py-2 text-gray-300">
              <ChevronRight className="w-4 h-4" />
            </td>
            <td className="px-4 py-2">{row.num}</td>
            <td className="px-4 py-2 font-mono text-xs break-all">{row.ka}</td>
            <td className="px-4 py-2 font-mono text-xs">{row.gtin || '—'}</td>
            <td className="px-4 py-2 text-center">{row.pallet_num ?? '—'}</td>
            <td className="px-4 py-2">{normalizeDate(row.production_date) || '—'}</td>
            <td className="px-4 py-2">{normalizeDate(row.expiry_date) || '—'}</td>
            <td className="px-4 py-2">
              <PrintedCell printed={row.printed} />
            </td>
            <td className="px-4 py-2">
              <WorkerCell worker={row.worker} />
            </td>
            <td className="px-4 py-2">
              <ActionButtons
                onEditDates={() => onEditDates(row)}
                onPrint={() => onPrintBox(row)}
                onPrintBundle={onPrintBoxBundle ? () => onPrintBoxBundle(row) : undefined}
                printBundleTitle="Печать всех КМ коробки и КА"
                disabled={printing}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PalletsTable({
  rows,
  onEditDates,
  onPrintPallet,
  onPrintPalletBoxes,
  printing,
}: {
  rows: TaskPalletRow[]
  onEditDates: (row: TaskPalletRow) => void
  onPrintPallet: (row: TaskPalletRow) => void
  onPrintPalletBoxes?: (row: TaskPalletRow) => void
  printing: boolean
}) {
  if (!rows.length) {
    return <EmptyState text="Нет паллет" />
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <tr>
          <th className="px-3 py-2 w-8" />
          <th className="px-4 py-2">№</th>
          <th className="px-4 py-2">Код агрегации (КА)</th>
          <th className="px-4 py-2">GTIN</th>
          <th className="px-4 py-2">Коробок</th>
          <th className="px-4 py-2">Дата производства</th>
          <th className="px-4 py-2">Срок годности</th>
          <th className="px-4 py-2">Напечатана</th>
          <th className="px-4 py-2">Работник</th>
          <th className="px-4 py-2">Действия</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.num} className="hover:bg-gray-50">
            <td className="px-3 py-2 text-gray-300">
              <ChevronRight className="w-4 h-4" />
            </td>
            <td className="px-4 py-2">{row.num}</td>
            <td className="px-4 py-2 font-mono text-xs break-all">{row.ka}</td>
            <td className="px-4 py-2 font-mono text-xs">{row.gtin || '—'}</td>
            <td className="px-4 py-2 text-center">{row.boxes_count}</td>
            <td className="px-4 py-2">{normalizeDate(row.production_date) || '—'}</td>
            <td className="px-4 py-2">{normalizeDate(row.expiry_date) || '—'}</td>
            <td className="px-4 py-2">
              <PrintedCell printed={row.printed} />
            </td>
            <td className="px-4 py-2">
              <WorkerCell worker={row.worker} />
            </td>
            <td className="px-4 py-2">
              <ActionButtons
                onEditDates={() => onEditDates(row)}
                onPrint={() => onPrintPallet(row)}
                onPrintPalletBoxes={
                  onPrintPalletBoxes ? () => onPrintPalletBoxes(row) : undefined
                }
                disabled={printing}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function EmptyState({ text }: { text: string }) {
  return <div className="text-center text-gray-400 py-16">{text}</div>
}

export function TaskEntitiesPanel({ task }: TaskEntitiesPanelProps) {
  const defaultTab: TaskEntityTab =
    task.codes_count > 0 ? 'codes' : task.boxes_count > 0 ? 'boxes' : 'pallets'

  const [activeTab, setActiveTab] = useState<TaskEntityTab>(defaultTab)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [datesTarget, setDatesTarget] = useState<AssignDatesTarget | null>(null)
  const [kmModalOpen, setKmModalOpen] = useState(false)

  const { data: flags } = useFeatureFlags()
  const print = usePrintActions(task.uuid)
  const separateKmKa = flags?.enable_separate_km_ka_print ?? false

  useEffect(() => {
    setActiveTab(
      task.codes_count > 0 ? 'codes' : task.boxes_count > 0 ? 'boxes' : 'pallets',
    )
    setPage(1)
    setSearchInput('')
    setSearch('')
  }, [task.uuid])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [activeTab])

  const queryParams = {
    taskUuid: task.uuid,
    tab: activeTab,
    activeTab,
    page,
    search,
  }

  const kmQuery = useTaskKMCodes({ ...queryParams, tab: 'codes' })
  const boxesQuery = useTaskBoxes({ ...queryParams, tab: 'boxes' })
  const palletsQuery = useTaskPallets({ ...queryParams, tab: 'pallets' })

  const activeQuery =
    activeTab === 'codes' ? kmQuery : activeTab === 'boxes' ? boxesQuery : palletsQuery

  const codesTabCount = Math.max(task.codes_count, kmQuery.data?.total ?? 0)
  const boxesTabCount = boxesQuery.data?.total ?? task.boxes_count
  const palletsTabCount = palletsQuery.data?.total ?? task.pallets_count

  const total =
    activeTab === 'codes'
      ? codesTabCount
      : activeTab === 'boxes'
        ? boxesTabCount
        : palletsTabCount

  const searchPlaceholder = activeTab === 'codes' ? '№ или КМ...' : '№ или КА...'

  const sectionTitle =
    activeTab === 'codes'
      ? 'Коды маркировки (КМ)'
      : activeTab === 'boxes'
        ? 'Коробки (КА)'
        : 'Паллеты (КА)'

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="border-b px-4 pt-2">
        <div className="flex gap-6">
          {(['codes', 'boxes', 'pallets'] as TaskEntityTab[]).map((tab) => {
            const count =
              tab === 'codes'
                ? codesTabCount
                : tab === 'boxes'
                  ? boxesTabCount
                  : palletsTabCount
            const active = activeTab === tab
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {TAB_LABELS[tab]} ({count})
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap">
        <h2 className="text-base font-semibold text-gray-900 shrink-0">{sectionTitle}</h2>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          {activeTab === 'codes' && task.codes_count > 0 && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0"
                onClick={() => print.renderAllKm()}
                isLoading={print.isRenderingKm}
                disabled={print.isPrinting}
              >
                <Image className="w-3.5 h-3.5 mr-1" /> Рендер КМ
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0"
                onClick={() => setKmModalOpen(true)}
                disabled={print.isPrinting}
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> КМ
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0"
                onClick={() => print.printKmAll()}
                isLoading={print.isPrinting}
              >
                Все КМ
              </Button>
            </>
          )}
          <div className="relative flex-1 min-w-[10rem] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto min-h-[320px]">
        {activeQuery.isLoading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : activeTab === 'codes' ? (
          <CodesTable rows={kmQuery.data?.codes ?? []} />
        ) : activeTab === 'boxes' ? (
          <BoxesTable
            rows={boxesQuery.data?.boxes ?? []}
            printing={print.isPrinting}
            onPrintBox={(row) => print.printBox(row.num)}
            onPrintBoxBundle={
              separateKmKa ? (row) => print.printBoxBundle(row.num) : undefined
            }
            onEditDates={(row) =>
              setDatesTarget({
                scope: 'box',
                num: row.num,
                productionDate: row.production_date,
                expiryDate: row.expiry_date,
              })
            }
          />
        ) : (
          <PalletsTable
            rows={palletsQuery.data?.pallets ?? []}
            printing={print.isPrinting}
            onPrintPallet={(row) => print.printPallet(row.num)}
            onPrintPalletBoxes={
              separateKmKa ? (row) => print.printPalletBoxes(row.num) : undefined
            }
            onEditDates={(row) =>
              setDatesTarget({
                scope: 'pallet',
                num: row.num,
                productionDate: row.production_date,
                expiryDate: row.expiry_date,
              })
            }
          />
        )}
      </div>

      <TablePagination total={total} page={page} onPageChange={setPage} />

      <AssignDatesModal
        taskUuid={task.uuid}
        target={datesTarget}
        onClose={() => setDatesTarget(null)}
      />

      <PrintKmModal
        open={kmModalOpen}
        onClose={() => setKmModalOpen(false)}
        isLoading={print.isPrinting}
        onPrint={(nums) => print.printKm(nums)}
      />

      <BrowserPrintPreviewModal
        open={print.browserPreview != null}
        title={print.browserPreview?.title ?? 'Предпросмотр печати'}
        labels={print.browserPreview?.labels ?? []}
        expectedCount={print.browserPreview?.expectedCount}
        isLoading={print.isFetchingLabels}
        loadProgress={print.fetchProgress}
        isPrinting={print.isBrowserPrinting}
        onClose={print.closeBrowserPreview}
        onPrint={print.confirmBrowserPrint}
      />
    </div>
  )
}
